import { titlecaseSentence } from "src/utils";

const parseChecklistItem = (item?: string) => {
  if (!item) {
    return { checklist: null, line: null };
  }

  const separator = " :\n";
  const index = item.indexOf(separator);
  if (index === -1) {
    return { checklist: null, line: item.trim() || null };
  }

  return {
    checklist: item.slice(0, index).trim() || null,
    line: item.slice(index + separator.length).trim() || null,
  };
};

const formatActionLabel = (
  action: string,
  subject: string | null | undefined,
  detail?: string | null,
) => {
  if (subject && detail) {
    return `${action} · ${subject} — ${detail}`;
  }
  if (subject) {
    return `${action} · ${subject}`;
  }
  if (detail) {
    return `${action} · ${detail}`;
  }
  return action;
};

const formatChecklistActionLabel = (
  action: string,
  checklist: string | null,
  line: string | null,
) => formatActionLabel(action, checklist, line);

const formatLogbookAction = (action: string, body: Record<string, any>) =>
  formatActionLabel(action, body._lbname);

export const getPendingRequestAction = (
  requestType: string,
  body: Record<string, any>,
) => {
  const apiRequest = body.request;
  const params = body.params ?? {};

  if (
    requestType === "checklist-action" ||
    apiRequest === "record-action-checklist" ||
    apiRequest === "complete-checklist"
  ) {
    const { checklist, line } = parseChecklistItem(params.item);

    if (apiRequest === "complete-checklist") {
      return formatChecklistActionLabel("Complete", checklist, line);
    }

    const verb = params.action
      ? titlecaseSentence(String(params.action))
      : "Checklist tick";

    return formatChecklistActionLabel(verb, checklist, line);
  }

  if (requestType === "save-entry") {
    if (apiRequest === "auto-save-entry" || apiRequest === "draft-save-entry") {
      return formatLogbookAction("Autosave", body);
    }

    const isNewEntry =
      params.lbeid === "new" ||
      params.meid === "new" ||
      params.partid === "new";
    return formatLogbookAction(
      isNewEntry ? "Create entry" : "Save entry",
      body,
    );
  }

  if (requestType === "withdraw-entry") {
    return formatLogbookAction("Withdraw entry", body);
  }

  return formatLogbookAction(
    titlecaseSentence(requestType.replaceAll("-", " ")),
    body,
  );
};

export const readPendingBody = (data: string) => {
  try {
    return JSON.parse(data) as Record<string, any>;
  } catch {
    return null;
  }
};

// Once several checklist rows are folded together, the badge carries the
// count; the headline only needs to say that the checklist changed.
export const getConsolidatedChecklistAction = (
  group: Array<{ data: string }>,
) => {
  const bodies = group
    .map(row => readPendingBody(row.data))
    .filter((body): body is Record<string, any> => body !== null);

  const hasComplete = bodies.some(
    body => body.request === "complete-checklist",
  );
  const checklist = bodies
    .map(body => parseChecklistItem(body.params?.item).checklist)
    .find(Boolean);
  const action = hasComplete ? "Complete" : "Checklist changed";

  return formatChecklistActionLabel(action, checklist ?? null, null);
};
